/**
 * Builds the flat search index from the app's structured catalogs.
 *
 * IMPORTANT: `navGroups` must be the SAME, already-permission-filtered groups
 * the sidebar renders (buildManageMenuGroups → the layout's filter). This
 * module does NOT re-implement permission gating for nav pages — it indexes
 * exactly what the user can already see, so search can never surface a page
 * they can't open. Reports honor `ccStaffOnly` via the `isCCStaff` flag;
 * settings mirror the (currently ungated) settings landing page.
 */
import type {
  NavGroup,
  NavItem,
  NavLeafItem,
  NavNestedItem,
} from "../components/layout/nav/navConfig";
import type { ReportCategory } from "../components/reports/reportsConfig";
import type { SettingsCategory } from "../components/settings/settingsConfig";
import type { SearchItem } from "./searchTypes";

function isNested(item: NavItem): item is NavNestedItem {
  return (item as NavNestedItem).isNested === true;
}
function isLeaf(item: NavItem): item is NavLeafItem {
  return typeof (item as NavLeafItem).to === "string";
}

function navItemToSearchItems(item: NavItem, category: string): SearchItem[] {
  if (isNested(item)) {
    return item.items.map((leaf) => ({
      id: `page:${leaf.to}`,
      label: leaf.label,
      route: leaf.to,
      group: "Page" as const,
      category,
    }));
  }
  if (isLeaf(item)) {
    return [
      {
        id: `page:${item.to}`,
        label: item.label,
        route: item.to,
        group: "Page",
        category,
      },
    ];
  }
  // NavCategoryItem — a non-link header, nothing to navigate to.
  return [];
}

export interface BuildSearchIndexOptions {
  /** Already-filtered nav groups (what the user actually sees in the sidebar). */
  navGroups: NavGroup[];
  reportCategories: ReportCategory[];
  settingsCategories: SettingsCategory[];
  /** tenant slug, used to build absolute routes for reports/settings */
  tenantId: string;
  /** platform staff — gates ccStaffOnly reports (e.g. Email Verification) */
  isCCStaff: boolean;
}

// Nav sectionKeys (from navConfig) whose visibility gates the reports /
// settings catalogs in search. Both sections are adminOnly in navConfig, so
// the layout's filter strips them from a non-admin's groups — and search must
// match: a financial report must NOT appear for someone who can't open it.
// Keyed off the SAME filtered nav the sidebar renders (single source of truth).
const REPORTS_NAV_SECTION_KEY = "reporting";
const SETTINGS_NAV_SECTION_KEY = "settings";

/** True if the (already-filtered) nav contains a section with this key. */
export function navHasSection(navGroups: NavGroup[], sectionKey: string): boolean {
  return navGroups.some((g) => g.sections.some((s) => s.sectionKey === sectionKey));
}

export function buildSearchIndex(opts: BuildSearchIndexOptions): SearchItem[] {
  const { navGroups, reportCategories, settingsCategories, tenantId, isCCStaff } = opts;
  const items: SearchItem[] = [];

  // 1. Nav pages — flatten the (filtered) sidebar tree.
  const sectionKeys = new Set<string>();
  for (const group of navGroups) {
    for (const section of group.sections) {
      sectionKeys.add(section.sectionKey);
      if (section.locked) continue; // hard-gated module not entitled
      for (const item of section.items) {
        items.push(...navItemToSearchItems(item, section.title));
      }
    }
  }

  // 2. Reports — ONLY if the user can see the Reports nav section (permission
  //    gate). Routes relative to /:tenant/manage/reports/.
  if (sectionKeys.has(REPORTS_NAV_SECTION_KEY)) {
    for (const cat of reportCategories) {
      for (const link of cat.links) {
        if (link.ccStaffOnly && !isCCStaff) continue;
        items.push({
          id: `report:${link.path}`,
          label: link.label,
          route: `/${tenantId}/manage/reports/${link.path}`,
          group: "Report",
          category: cat.title,
        });
      }
    }
  }

  // 3. Settings — ONLY if the user can see the Settings nav section.
  //    `to` is already relative to /:tenant/.
  if (sectionKeys.has(SETTINGS_NAV_SECTION_KEY)) {
    for (const cat of settingsCategories) {
      for (const link of cat.links) {
        items.push({
          id: `setting:${link.to}`,
          label: link.label,
          route: `/${tenantId}/${link.to}`,
          group: "Setting",
          category: cat.title,
          keywords: settingKeywords(link),
        });
      }
    }
  }

  // De-dupe by route (a page can appear in more than one nav section); keep
  // first. The dropped duplicate's keywords move onto the kept item: a
  // settings page that is ALSO in the sidebar is indexed as the nav page, and
  // without this its description and aliases would be thrown away with the
  // duplicate, so the alias would find nothing.
  const byRoute = new Map<string, SearchItem>();
  const out: SearchItem[] = [];
  for (const it of items) {
    const kept = byRoute.get(it.route);
    if (!kept) {
      byRoute.set(it.route, it);
      out.push(it);
      continue;
    }
    if (it.keywords?.length) {
      kept.keywords = Array.from(new Set([...(kept.keywords || []), ...it.keywords]));
    }
  }
  return out;
}

/**
 * What a settings page is matched on besides its label: its description and
 * its aliases (`keywords` in settingsConfig — the words staff actually type,
 * e.g. "disclaimers" for Waivers).
 */
function settingKeywords(link: { description?: string; keywords?: string[] }): string[] | undefined {
  const all = [link.description, ...(link.keywords || [])].filter(
    (k): k is string => typeof k === "string" && k.trim() !== ""
  );
  return all.length ? all : undefined;
}

/** Minimal shape of a KB article (decoupled from the knowledgebase API type). */
export interface KbArticleLike {
  id: string;
  title: string;
  excerpt?: string | null;
  tags?: string[] | null;
  kb_categories?: { name?: string } | null;
}

/**
 * Map KB articles (already audience-filtered server-side) into Help search
 * items. Routes deep-link the knowledgebase page via ?article=<id>; the
 * excerpt + tags become match keywords so "how do I…" phrasing lands.
 */
export function kbArticlesToSearchItems(
  articles: KbArticleLike[],
  tenantId: string
): SearchItem[] {
  return articles.map((a) => ({
    id: `kb:${a.id}`,
    label: a.title,
    route: `/${tenantId}/manage/knowledgebase?article=${a.id}`,
    group: "Help" as const,
    category: a.kb_categories?.name || "Help",
    keywords: [a.excerpt, ...(a.tags || [])].filter(Boolean) as string[],
  }));
}
